import {
  mapLeadDetail,
  mapRawnoteEdited,
  type LeadDetail,
  type PersonEditInput,
  type RawnoteEditFields,
  type RawnoteEdited,
} from '@viq-admin/screens';
import { superadminFetch } from './dataProvider';

/**
 * PATCH /v1/superadmin/leads/:id — cross-tenant lead edit. Keyed by lead id
 * only (no ?companyId=), matching the console's archive/restore/detail calls.
 */
export async function editLead(
  personId: string,
  fields: PersonEditInput,
  expectedVersion: number,
): Promise<LeadDetail['person']> {
  const body = await superadminFetch<{ person: unknown }>(`/v1/superadmin/leads/${personId}`, {
    method: 'PATCH',
    body: JSON.stringify({ ...fields, expectedVersion }),
  });
  return mapLeadDetail({ person: body.person }).person;
}

/**
 * PATCH /v1/superadmin/leads/:leadId/notes/:noteId — cross-tenant note edit.
 * Keyed by ids only (no ?companyId=), matching the console's other lead
 * routes. Sends ONLY the field being edited so the PATCH cannot blank the
 * other one.
 *
 * The response's `note` is the RETURNING row off the backend's UPDATE,
 * UNALIASED — i.e. snake_case, not the camelCase `RawnoteEdited` this
 * function returns. Typed `unknown` and run through the shared mapper, same
 * as `editLead` above does via `mapLeadDetail` on its `{ person }` response,
 * and same as the portal twin (`apps/portal/src/api/leadEdit.ts`). Do not
 * "simplify" this back to `body.note` — that would silently produce a
 * `RawnoteEdited` with `serverVersion`/`originalText`/`updatedAt` undefined.
 */
/**
 * POST /v1/superadmin/leads/:leadId/notes — add a note to any tenant's lead,
 * including one with no notes yet. Keyed by lead id only, like the edit.
 */
export async function createRawnote(leadId: string, text: string): Promise<RawnoteEdited> {
  const body = await superadminFetch<{ note: unknown }>(`/v1/superadmin/leads/${leadId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
  return mapRawnoteEdited(body.note);
}

export async function editRawnote(
  leadId: string,
  noteId: string,
  fields: RawnoteEditFields,
  expectedVersion: number,
): Promise<RawnoteEdited> {
  const body = await superadminFetch<{ note: unknown }>(
    `/v1/superadmin/leads/${leadId}/notes/${noteId}`,
    { method: 'PATCH', body: JSON.stringify({ ...fields, expectedVersion }) },
  );
  return mapRawnoteEdited(body.note);
}
